<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue';
import { usePrimeVue } from 'primevue/config';
import Button from 'primevue/button';

const props = defineProps<{
  files: File[];
}>();

const emits = defineEmits<{
  onRemoveFile: [number];
}>();

const $primevue = usePrimeVue();

const previews = ref<string[]>([]);

watch(
  () => props.files,
  (newFiles) => syncPreviews(newFiles),
  { immediate: true }
);

onBeforeUnmount(() => {
  previews.value.forEach((url) => URL.revokeObjectURL(url));
});

function formatSize(bytes: any) {
  const k = 1024;
  const dm = 3;
  const sizes = $primevue?.config?.locale?.fileSizeTypes;

  if (!sizes) return;
  if (bytes === 0) return `0 ${sizes[0]}`;

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

  return `${formattedSize} ${sizes[i]}`;
}

function generatePreviews(files: File[]) {
  previews.value?.forEach((url) => URL.revokeObjectURL(url));
  previews.value = files.map((file) => URL.createObjectURL(file));
}

function syncPreviews(newFiles: File[] | undefined) {
  if (Array.isArray(newFiles)) return generatePreviews(newFiles);

  previews.value?.forEach((url) => URL.revokeObjectURL(url));
  previews.value = [];
}
</script>

<template>
  <div class="grid grid-cols-2 items-center justify-center gap-1 p-0 sm:grid-cols-3 sm:gap-5">
    <div
      v-for="({ name, type, size }, index) of props.files"
      class="relative m-0 flex max-h-14 flex-row items-center gap-2 rounded-lg border border-solid p-2 text-center"
      :key="name + type + size"
    >
      <div
        class="h-8 w-12 overflow-hidden rounded bg-cover bg-center"
        :style="{ 'background-image': 'url(' + previews[index] + ')' }"
      ></div>

      <div class="flex w-full flex-col gap-1 overflow-hidden text-ellipsis text-nowrap text-start">
        <span
          v-tooltip.top="name"
          class="overflow-hidden text-ellipsis text-xs font-semibold"
        >
          {{ name }}
        </span>

        <span class="text-[10px]">{{ formatSize(size) }}</span>
      </div>

      <Button
        rounded
        class="pi pi-trash absolute bottom-0.5 right-0 flex w-fit items-center border-none bg-transparent p-1 text-xs text-red-600 hover:bg-red-100"
        pt:label="hidden"
        severity="danger"
        @click="emits('onRemoveFile', index)"
      />
    </div>
  </div>
</template>
